import { API_CONFIG } from '@/infrastructure/config/api';
import { createAuthenticatedSSE, type SSESubscription } from '@/shared/utils/fetchSSE';
import { get } from '@/shared/utils/fetchApi';

// Data client for the superadmin Agentic/Gateway log tabs (PBI 81485):
// `/agentic/logs`, `/gateway/logs` and the anonymized feeds.
// Same wire contract as the Assistant tab: `{ logFiles }` list, `{ lines, raw }`
// day snapshot, fetch-SSE `log` events with `{"line": "..."}` payloads.

export interface ServiceLogLine {
  id: string;
  timestamp: string;
  level: string;
  message: string;
}

export interface ServiceLogsResponse {
  lines?: ServiceLogLine[];
  raw?: string;
  logFiles?: string[];
  message?: string;
}

export interface ChatFeedItem {
  id: string;
  /** Pseudônimo estável da sessão — agrupa mensagens da mesma conversa. */
  sessionKey: string;
  promptAt: string;
  responseAt: string | null;
  channel: 'playground' | 'communicator' | 'mcp';
  organizationName: string | null;
  productName: string | null;
  userHash: string;
  prompt: string;
  response: string | null;
}

export interface ToolCallFeedItem {
  id: string;
  /** Pseudônimo estável do chamador (org + usuário + tipo de conexão + app OAuth). */
  sessionKey: string;
  timestamp: string;
  toolName: string;
  organizationName: string | null;
  productName: string | null;
  userHash: string;
  connectionType: 'oauth' | 'api_token' | 'service' | 'unknown';
  oauthClient: string | null;
  status: string;
  paramsSummary: string | null;
  /** Trecho truncado do que a tool retornou (mascarado no servidor); null p/ eventos antigos. */
  resultPreview: string | null;
  errorMessage: string | null;
}

export interface AgentExecutionFeedItem {
  id: string;
  /** Pseudônimo estável do AGENTE — agrupa as execuções do mesmo agente. */
  sessionKey: string;
  timestamp: string;
  agentName: string;
  organizationName: string | null;
  productName: string | null;
  status: string;
  durationMs: number | null;
  steps: number | null;
  totalTokens: number | null;
  errorMessage: string | null;
}

export type ServiceLogsTarget = 'agentic' | 'gateway' | 'engine';

const normalizeLogLevel = (level: string): string => {
  const upper = level.toUpperCase();
  switch (upper) {
    case 'INF':
      return 'INFO';
    case 'WRN':
    case 'WARN':
      return 'WARN';
    case 'ERR':
    case 'ERROR':
      return 'ERROR';
    case 'DBG':
    case 'DEBUG':
      return 'DEBUG';
    default:
      return upper;
  }
};

const parseLogLine = (line: string): ServiceLogLine | null => {
  // Nível = qualquer token não-espaço (não uma lista fixa): uma linha com nível
  // fora da lista (FTL, VRB, custom) manteria o timestamp original em vez de
  // cair no fallback com hora sintética. Mesma regra do parser do backend.
  const logRegex = /^\[(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})(?:\s+([^\s\]]+))?\]\s*(.*)$/i;
  const match = line.match(logRegex);

  if (match) {
    const [, timestamp, lvl, message] = match;
    return {
      id: crypto.randomUUID(),
      timestamp: timestamp.trim(),
      level: normalizeLogLevel(lvl || 'INFO'),
      message: message.trim(),
    };
  }

  if (line.trim()) {
    return {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString().replace('T', ' ').slice(0, 19),
      level: 'INFO',
      message: line.trim(),
    };
  }

  return null;
};

export class ServiceLogsClient {
  constructor(private readonly target: ServiceLogsTarget) {}

  /**
   * Sem `file`: lista de dias (`{ logFiles }`). Com `file`: conteúdo daquele dia
   * (`{ lines, raw }`), com `tail` linhas no final. Mesmo gotcha do Assistant:
   * `tail` sem `file` faria o upstream ler o arquivo de hoje — por isso só é
   * enviado junto com `file`.
   */
  async getLogs(file?: string, tail: number = 200): Promise<ServiceLogsResponse> {
    const params = new URLSearchParams();
    if (file) {
      params.append('file', file);
      params.append('tail', tail.toString());
    }
    const qs = params.toString();
    return get<ServiceLogsResponse>(qs ? `/${this.target}/logs?${qs}` : `/${this.target}/logs`);
  }

  createLogStream(
    onLog: (log: ServiceLogLine) => void,
    onConnect?: (data: unknown) => void,
    onError?: (event: Event) => void,
    file?: string,
    tail: number = 200
  ): SSESubscription {
    const params = new URLSearchParams();
    params.append('follow', 'true');
    params.append('tail', tail.toString());
    if (file) params.append('file', file);

    const url = `${API_CONFIG.API_ROOT_URL}/${this.target}/logs?${params.toString()}`;

    return createAuthenticatedSSE(url, {
      onOpen: () => {
        onConnect?.({ status: 'connected' });
      },
      onMessage: (data, eventType) => {
        if (eventType === 'connected') {
          try {
            onConnect?.(JSON.parse(data));
          } catch {
            // Non-JSON connected event — safe to ignore
          }
          return;
        }

        if (eventType === 'heartbeat') {
          return;
        }

        if (eventType === 'log') {
          try {
            const parsed = JSON.parse(data);
            const rawLine = parsed.line || String(parsed);
            const logLine = parseLogLine(rawLine.replace(/\r+$/, ''));
            if (logLine) onLog(logLine);
          } catch {
            const logLine = parseLogLine(data);
            if (logLine) onLog(logLine);
          }
        }
      },
      onError: (error) => {
        onError?.(new ErrorEvent('error', { error }));
      },
    });
  }
}

/**
 * Converte o texto Serilog cru em linhas estruturadas. Usado como fallback pelo
 * terminal quando o upstream devolve `raw` sem `lines` — sem isso o painel
 * mostraria um terminal vazio tendo conteúdo em mãos.
 */
export const parseRawLogText = (raw: string): ServiceLogLine[] =>
  raw
    .split('\n')
    .map((line) => parseLogLine(line.replace(/\r+$/, '')))
    .filter((line): line is ServiceLogLine => line !== null);

export const agenticLogsClient = new ServiceLogsClient('agentic');
export const gatewayLogsClient = new ServiceLogsClient('gateway');
export const engineLogsClient = new ServiceLogsClient('engine');

const feedQuery = (params: { date?: string | null; tail?: number }): string => {
  const sp = new URLSearchParams();
  if (params.date) sp.append('date', params.date);
  // Checagem explícita (não truthiness): tail = 0 é um valor que o caller pode
  // enviar de propósito e o servidor valida — descartá-lo mudaria a requisição.
  if (params.tail !== undefined && params.tail !== null) sp.append('tail', params.tail.toString());
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
};

export const getAgenticChats = (params: {
  date?: string | null;
  tail?: number;
}): Promise<{ items: ChatFeedItem[] }> =>
  get<{ items: ChatFeedItem[] }>(`/agentic/chats${feedQuery(params)}`);

export const getGatewayToolCalls = (params: {
  date?: string | null;
  tail?: number;
}): Promise<{ items: ToolCallFeedItem[] }> =>
  get<{ items: ToolCallFeedItem[] }>(`/gateway/tool-calls${feedQuery(params)}`);

export const getEngineAgentExecutions = (params: {
  date?: string | null;
  tail?: number;
}): Promise<{ items: AgentExecutionFeedItem[] }> =>
  get<{ items: AgentExecutionFeedItem[] }>(`/engine/agent-executions${feedQuery(params)}`);
